import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styled/theme';
import { Container, H3, TextMuted, Grid2, Card, ButtonLink, Flex } from '../styled/BaseComponents';
import { sendEmail } from '../../../../utils/services/user.service';

const OTCWrapper = styled.section`
  margin-top: 1.75rem;
`;

const OTCCard = styled(Card)`
  background: linear-gradient(180deg, rgba(92, 233, 255, 0.15), rgba(0, 247, 210, 0.12));
  padding: 1rem;
`;

const OTCTitle = styled(H3)`
  margin: 0;
  color: ${theme.colors.text};
`;

const BulletCard = styled(Card)`
  padding: 0.75rem;
  color: ${theme.colors.text};
`;

const Actions = styled(Flex)`
  flex-wrap: wrap;
  margin-top: 0.75rem;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalCard = styled(Card)`
  width: min(420px, 92vw);
  padding: 1.5rem;
  border-radius: 1rem;
  background: ${theme.colors.card};
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.text};
`;

const ModalTitle = styled.h4`
  margin: 0 0 1rem 0;
  font-size: 1.2rem;
  font-weight: 700;
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  text-align: left;
  font-size: 0.85rem;
  gap: 0.4rem;
`;

const Input = styled.input`
  padding: 0.65rem 0.8rem;
  border-radius: 0.6rem;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.card2};
  color: ${theme.colors.text};

  &:focus {
    outline: none;
    border-color: ${theme.colors.accent};
    box-shadow: 0 0 0 2px rgba(0, 247, 210, 0.2);
  }
`;

const TextArea = styled.textarea`
  padding: 0.65rem 0.8rem;
  min-height: 120px;
  border-radius: 0.6rem;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.card2};
  color: ${theme.colors.text};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${theme.colors.accent};
    box-shadow: 0 0 0 2px rgba(0, 247, 210, 0.2);
  }
`;

const ErrorText = styled.span`
  color: ${theme.colors.error};
  font-size: 0.75rem;
`;

const SubmitError = styled.div`
  color: ${theme.colors.error};
  font-size: 0.8rem;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const ActionButton = styled.button`
  padding: 0.55rem 1.1rem;
  border-radius: 0.6rem;
  border: 1px solid transparent;
  background: ${({ variant }) => (variant === 'primary' ? theme.colors.accent : 'transparent')};
  color: ${({ variant }) => (variant === 'primary' ? '#001018' : theme.colors.text)};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }
`;

export const OTC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [triggerLabel, setTriggerLabel] = useState('');
  const [formValues, setFormValues] = useState({
    subject: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const bullets = [
    '🔒 Custody — Segregated multi-sig vaults',
    '🧾 Compliance — KYC/KYB & monitoring',
    '⚡ Liquidity — RFQ engine + smart routing',
    '🔗 Traceability — On-chain attribution & risk scoring',
  ];

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const openModal = (defaultSubject) => {
    setTriggerLabel(defaultSubject);
    setFormValues({
      subject: defaultSubject,
      email: '',
      message: '',
    });
    setErrors({});
    setSubmitError('');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    if (isSubmitting) return;
    setIsModalOpen(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const nextErrors = {};

    if (!formValues.subject.trim()) {
      nextErrors.subject = 'Subject is required';
    }
    if (!formValues.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!emailPattern.test(formValues.email.trim())) {
      nextErrors.email = 'Enter a valid email address';
    }
    if (!formValues.message.trim()) {
      nextErrors.message = 'Message is required';
    }

    setErrors(nextErrors);
    setSubmitError('');

    if (Object.keys(nextErrors).length === 0) {
      try {
        setIsSubmitting(true);
        const payload = {
          subject: formValues.subject.trim(),
          email: formValues.email.trim(),
          message: formValues.message.trim(),
        };
        await sendEmail(payload);
        setIsSubmitting(false);
        setIsModalOpen(false);
      } catch (error) {
        console.error('Error sending OTC email:', error);
        setSubmitError('We could not send your request. Please try again.');
        setIsSubmitting(false);
      }
    }
  };

  return (
    <OTCWrapper>
      <Container>
        <OTCCard>
          <OTCTitle>OTC Spotlight</OTCTitle>
          <TextMuted style={{ marginTop: 6 }}>
            Deep liquidity for BTC/ETH/stables with settlement controls and on-chain traceability.
          </TextMuted>
          <Grid2 style={{ marginTop: '0.75rem' }}>
            {bullets.map((bullet, idx) => (
              <BulletCard key={idx}>{bullet}</BulletCard>
            ))}
          </Grid2>
          <Actions gap="0.5rem">
            <ButtonLink
              as="button"
              type="button"
              onClick={() => openModal('Request quote')}
            >
              Request quote
            </ButtonLink>
            <ButtonLink
              as="button"
              type="button"
              onClick={() => openModal('Talk to desk')}
            >
              Talk to desk
            </ButtonLink>
          </Actions>
        </OTCCard>
        {isModalOpen && (
          <ModalOverlay role="dialog" aria-modal="true" onClick={closeModal}>
            <ModalCard onClick={(e) => e.stopPropagation()}>
              <ModalTitle>{triggerLabel || 'Contact OTC Desk'}</ModalTitle>
              <ModalForm onSubmit={handleSubmit} noValidate>
                <Label>
                  Subject
                  <Input
                    name="subject"
                    value={formValues.subject}
                    onChange={handleChange}
                    placeholder="Enter subject"
                    autoFocus
                  />
                  {errors.subject && <ErrorText>{errors.subject}</ErrorText>}
                </Label>
                <Label>
                  Email
                  <Input
                    name="email"
                    type="email"
                    value={formValues.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                  />
                  {errors.email && <ErrorText>{errors.email}</ErrorText>}
                </Label>
                <Label>
                  Message
                  <TextArea
                    name="message"
                    value={formValues.message}
                    onChange={handleChange}
                    placeholder="How can we help you?"
                  />
                  {errors.message && <ErrorText>{errors.message}</ErrorText>}
                </Label>
                {submitError && <SubmitError>{submitError}</SubmitError>}
                <ModalActions>
                  <ActionButton type="button" onClick={closeModal} disabled={isSubmitting}>
                    Cancel
                  </ActionButton>
                  <ActionButton variant="primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending…' : 'Submit'}
                  </ActionButton>
                </ModalActions>
              </ModalForm>
            </ModalCard>
          </ModalOverlay>
        )}
      </Container>
    </OTCWrapper>
  );
};

export default OTC;
